import {createResource, getResource, updateResource, OkDocument, ErrorDocument} from "./common"
import {Result} from "neverthrow"

// Fee types for tiered pricing
export type FeeType = "Fixed" | "Percentage"

export type FeeRange = {
	lowerBound: number // In cents
	upperBound: number | null // null means no upper limit (infinity)
}

// Flattened tier structure - type and value are at the same level as range
export type FeeTier = {
	type: FeeType
	value: number // In cents for Fixed, in percentage (1 = 1%) for Percentage
	range: FeeRange
}

export type TieredFee = {
	tiers: FeeTier[]
}

export type PaymentMethodTerms = {
	allowed: boolean
	fee?: TieredFee | null
}

export type BillpayTerms = {
	type: "billpayTerms"
	id: string
	attributes: {
		name: string
		createdAt: Date
		updatedAt: Date
		status: "Active" | "Archived"
		externalFunding: boolean
		tags?: Record<string, any>
		ach: PaymentMethodTerms
		check: PaymentMethodTerms
		wire: PaymentMethodTerms
		sameDayAch: PaymentMethodTerms
	}
	relationships: {
		org: {
			data: {
				type: "org"
				id: string
			}
		}
	}
}

// Helper function to convert tiered fees from cents to dollars for display
function convertTieredFeeFromCents(fee: TieredFee | null | undefined): TieredFee | null {
	if (!fee || !fee.tiers) return null
	return {
		tiers: fee.tiers.map((tier) => ({
			type: tier.type,
			// Fixed fees are in cents, convert to dollars. Percentage stays as-is.
			value: tier.type === "Fixed" ? tier.value / 100 : tier.value,
			range: {
				// Range bounds are in cents, convert to dollars
				lowerBound: tier.range.lowerBound / 100,
				upperBound: tier.range.upperBound != null ? tier.range.upperBound / 100 : null,
			},
		})),
	}
}

// Helper function to convert tiered fees from dollars to cents for API
function convertTieredFeeToCents(fee: TieredFee | null | undefined): TieredFee | undefined {
	if (!fee || !fee.tiers) return undefined
	return {
		tiers: fee.tiers.map((tier) => ({
			type: tier.type,
			// Fixed fees need conversion to cents. Percentage stays as-is.
			value: tier.type === "Fixed" ? Math.round(tier.value * 100) : tier.value,
			range: {
				// Range bounds need conversion to cents
				lowerBound: Math.round(tier.range.lowerBound * 100),
				upperBound: tier.range.upperBound != null ? Math.round(tier.range.upperBound * 100) : null,
			},
		})),
	}
}

// Helper function to convert payment method terms from API response
function convertPaymentMethodFromApi(method: PaymentMethodTerms): PaymentMethodTerms {
	return {
		allowed: method.allowed,
		fee: convertTieredFeeFromCents(method.fee),
	}
}

// Helper function to convert BillpayTerms attributes from API response
function convertBillpayTermsAttributes(attributes: BillpayTerms["attributes"]): BillpayTerms["attributes"] {
	return {
		...attributes,
		externalFunding: attributes.externalFunding ?? false,
		ach: convertPaymentMethodFromApi(attributes.ach),
		check: convertPaymentMethodFromApi(attributes.check),
		wire: convertPaymentMethodFromApi(attributes.wire),
		sameDayAch: convertPaymentMethodFromApi(attributes.sameDayAch),
	}
}

export async function findBillpayTerms(
	accessToken: string,
	offset: number,
	limit: number,
	orgId?: string
): Promise<Result<OkDocument<Array<BillpayTerms>>, ErrorDocument>> {
	const query: any = {
		page: {
			limit,
			offset,
		},
		include: "org",
	}

	if (orgId && orgId !== "-1") {
		const parsedOrgId = parseInt(orgId, 10)
		if (!isNaN(parsedOrgId) && parsedOrgId > 0) {
			query.filter = {orgId: parsedOrgId}
		}
	}

	const result = await getResource<Array<BillpayTerms>>("billpay/terms", accessToken, query)

	// Convert fees from cents to dollars for display
	return result.map((v) => ({
		...v,
		data: v.data.map((terms) => ({
			...terms,
			attributes: convertBillpayTermsAttributes(terms.attributes),
		})),
	}))
}

export async function getBillpayTerms(accessToken: string, id: string): Promise<Result<BillpayTerms, ErrorDocument>> {
	const query = {
		include: "org",
	}
	const result = await getResource<BillpayTerms>(`billpay/terms/${id}`, accessToken, query)

	return result.map((v) => ({
		...v.data,
		attributes: convertBillpayTermsAttributes(v.data.attributes),
	}))
}

// Helper function to build the API payload for a payment method
function buildPaymentMethodPayload(method: PaymentMethodTerms) {
	const feeInCents = convertTieredFeeToCents(method.fee)
	const payload: {allowed: boolean; fee?: TieredFee} = {
		allowed: method.allowed,
	}
	// Only include fee when allowed is true and fee exists
	// When allowed is false, omit fee field (API expects object or omitted, not null)
	if (method.allowed && feeInCents) {
		payload.fee = feeInCents
	}
	return payload
}

export async function createBillpayTerms(
	accessToken: string,
	name: string,
	orgId: string,
	externalFunding: boolean,
	ach: PaymentMethodTerms,
	check: PaymentMethodTerms,
	wire: PaymentMethodTerms,
	sameDayAch: PaymentMethodTerms,
	idempotencyKey: string
): Promise<Result<BillpayTerms, ErrorDocument>> {
	const result = await createResource<BillpayTerms>("billpay/terms", accessToken, {
		type: "billpayTerms",
		attributes: {
			name,
			idempotencyKey,
			externalFunding,
			ach: buildPaymentMethodPayload(ach),
			check: buildPaymentMethodPayload(check),
			wire: buildPaymentMethodPayload(wire),
			sameDayAch: buildPaymentMethodPayload(sameDayAch),
		},
		relationships: {
			org: {
				data: {
					type: "org",
					id: orgId,
				},
			},
		},
	})
	// Convert fees from cents to dollars for display
	return result.map((v) => ({
		...v.data,
		attributes: convertBillpayTermsAttributes(v.data.attributes),
	}))
}

export async function updateBillpayTerms(
	accessToken: string,
	id: string,
	name: string | undefined,
	externalFunding: boolean,
	ach: PaymentMethodTerms,
	check: PaymentMethodTerms,
	wire: PaymentMethodTerms,
	sameDayAch: PaymentMethodTerms,
	idempotencyKey: string
): Promise<Result<BillpayTerms, ErrorDocument>> {
	const attributes: any = {
		idempotencyKey,
		externalFunding,
		ach: buildPaymentMethodPayload(ach),
		check: buildPaymentMethodPayload(check),
		wire: buildPaymentMethodPayload(wire),
		sameDayAch: buildPaymentMethodPayload(sameDayAch),
	}

	if (name !== undefined) {
		attributes.name = name
	}

	const result = await updateResource<BillpayTerms>(`billpay/terms/${id}`, accessToken, {
		type: "billpayTerms",
		attributes,
	})
	// Convert fees from cents to dollars for display
	return result.map((v) => ({
		...v.data,
		attributes: convertBillpayTermsAttributes(v.data.attributes),
	}))
}
